/**
 * Runs inside the hidden frame the assistant panel opens: waits until the
 * page has drawn the element the model asked for and stopped changing, then
 * either reads it as a text outline (assistant-view-outline.ts) or photographs
 * it with snapDOM (as the support bubble does, support-capture-engines.ts), and
 * posts the result to the panel. Renders nothing.
 */
import { snapdom } from "@zumer/snapdom";
import { useEffect } from "react";
import type { ViewMode } from "../../api/assistant/assistant";
import { outlinePage } from "./assistant-view-outline";
import {
	FOCUS_PATTERN,
	VIEW_MESSAGE_TYPE,
	type ViewCaptureMessage,
} from "./assistant-view-protocol";

/** How long the element may take to appear (its data loads first). */
const FIND_TIMEOUT_MS = 10_000;
/** The page counts as drawn once nothing in it changed for this long. */
const QUIET_MS = 800;
/** Never wait longer than this for it to go quiet; take the picture anyway. */
const SETTLE_TIMEOUT_MS = 5_000;
const POLL_MS = 200;
/** The model reads the text; a whole page's worth would crowd out the rest. */
const TEXT_MAX_CHARS = 2_000;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const waitForElement = async (focus: string): Promise<HTMLElement | null> => {
	const deadline = Date.now() + FIND_TIMEOUT_MS;
	while (Date.now() < deadline) {
		const element = focus
			? document.querySelector<HTMLElement>(focus)
			: document.body;
		if (element && element.getBoundingClientRect().height > 0) {
			return element;
		}
		await sleep(POLL_MS);
	}
	return null;
};

const waitUntilQuiet = (): Promise<void> =>
	new Promise((resolve) => {
		let timer = window.setTimeout(done, QUIET_MS);
		const giveUp = window.setTimeout(done, SETTLE_TIMEOUT_MS);
		const observer = new MutationObserver(() => {
			window.clearTimeout(timer);
			timer = window.setTimeout(done, QUIET_MS);
		});
		observer.observe(document.body, {
			childList: true,
			subtree: true,
			attributes: true,
			characterData: true,
		});
		function done() {
			observer.disconnect();
			window.clearTimeout(timer);
			window.clearTimeout(giveUp);
			resolve();
		}
	});

type CaptureResult = Omit<ViewCaptureMessage, "type" | "token">;

const failed = (text: string): CaptureResult => ({
	found: false,
	text,
	png: "",
	outline: "",
	outline_truncated: false,
});

const read = async (
	focus: string,
	mode: ViewMode,
	outlineMaxChars: number
): Promise<CaptureResult> => {
	if (!FOCUS_PATTERN.test(focus)) {
		return failed(`"${focus}" is not an allowed focus`);
	}
	const element = await waitForElement(focus);
	if (!element) {
		return failed(
			`Nothing matching ${focus || "the page"} appeared within ${FIND_TIMEOUT_MS / 1000} s`
		);
	}
	await waitUntilQuiet();
	if (mode === "outline") {
		const { text, truncated } = outlinePage(element, outlineMaxChars);
		return {
			found: true,
			text: "",
			png: "",
			outline: text,
			outline_truncated: truncated,
		};
	}
	const canvas = await snapdom.toCanvas(element, {
		...(focus ? {} : { clip: "viewport" as const }),
		backgroundColor: getComputedStyle(document.body).backgroundColor,
		dpr: 1,
		reconcile: true,
	});
	return {
		found: true,
		text: element.innerText.slice(0, TEXT_MAX_CHARS),
		png: canvas.toDataURL("image/png").split(",", 2)[1],
		outline: "",
		outline_truncated: false,
	};
};

export const AssistantViewCapture = ({
	focus,
	token,
	mode,
	outlineMaxChars,
}: {
	focus: string;
	token: string;
	mode: ViewMode;
	outlineMaxChars: number;
}) => {
	useEffect(() => {
		let cancelled = false;
		read(focus, mode, outlineMaxChars)
			.catch((error: Error) =>
				failed(`The page could not be read: ${error.message}`)
			)
			.then((result) => {
				if (cancelled) return;
				const message: ViewCaptureMessage = {
					type: VIEW_MESSAGE_TYPE,
					token,
					...result,
				};
				// "/" = the panel's own origin, whatever this page's origin is.
				window.parent.postMessage(message, "/");
			});
		return () => {
			cancelled = true;
		};
	}, [focus, token, mode, outlineMaxChars]);
	return null;
};
